/**
 * Multi-select up to DESKTOP_MAX_COLS — visuals from agenda-day-v1.html picker.
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CONTENT_MAX_WIDTH_COMPACT } from '../../shared/layout/breakpoints';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { DESKTOP_MAX_COLS } from '../domain/dayBoardLayout';
import { agenda, agendaDotColor, agendaFonts, agendaPaperSheet } from '../domain/agendaTheme';
import type { AppointmentResource } from '../domain/types';

type Props = {
  visible: boolean;
  resources: AppointmentResource[];
  selectedIds: string[];
  dayOfWeek: number;
  /** Pref local — muestra turnos cancelados apagados en tablero y lista. */
  showCancelled: boolean;
  onShowCancelledChange: (v: boolean) => void;
  onClose: () => void;
  onApply: (ids: string[]) => void;
};

function hasHoursToday(r: AppointmentResource, dayOfWeek: number): boolean {
  return r.hours.some((h) => h.dayOfWeek === dayOfWeek && h.endTime > h.startTime);
}

export function AgendaStaffPickerModal(props: Props) {
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const [draft, setDraft] = useState<string[]>(props.selectedIds);

  useEffect(() => {
    if (props.visible) setDraft(props.selectedIds.slice(0, DESKTOP_MAX_COLS));
  }, [props.visible, props.selectedIds]);

  const atMax = draft.length >= DESKTOP_MAX_COLS;
  const toggle = (id: string) => {
    setDraft((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= DESKTOP_MAX_COLS) return prev;
      return [...prev, id];
    });
  };

  const apply = () => {
    if (draft.length === 0) return;
    props.onApply(draft.slice(0, DESKTOP_MAX_COLS));
  };

  return (
    <Modal visible={props.visible} transparent animationType="fade" onRequestClose={props.onClose}>
      <View style={styles.backdrop}>
        <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={props.onClose} />
        <View
          style={[
            styles.card,
            {
              maxHeight: Math.min(520, height * 0.9),
              maxWidth: CONTENT_MAX_WIDTH_COMPACT,
              paddingBottom: Math.max(insets.bottom, spacing.sm),
            },
          ]}
        >
          <View style={styles.header}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.title}>Elegir agendas</Text>
              <Text style={styles.subtitle}>
                Marcá de 1 a {DESKTOP_MAX_COLS} para verlas lado a lado en el día.
              </Text>
            </View>
            <TouchableOpacity onPress={props.onClose} hitSlop={10} accessibilityLabel="Cerrar">
              <Text style={styles.close}>×</Text>
            </TouchableOpacity>
          </View>

          <Text style={[styles.count, draft.length === 0 && styles.countBad]}>
            {draft.length} / {DESKTOP_MAX_COLS} seleccionadas
          </Text>

          <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
            {props.resources.map((r) => {
              const on = draft.includes(r.id);
              const blocked = !on && atMax;
              const open = hasHoursToday(r, props.dayOfWeek);
              return (
                <TouchableOpacity
                  key={r.id}
                  style={[styles.row, on && styles.rowOn, blocked && styles.rowBlocked]}
                  onPress={() => {
                    if (blocked) return;
                    toggle(r.id);
                  }}
                  disabled={blocked}
                  activeOpacity={0.7}
                >
                  <View style={[styles.check, on && styles.checkOn]}>
                    {on ? <Text style={styles.checkMark}>✓</Text> : null}
                  </View>
                  <View style={[styles.dot, { backgroundColor: agendaDotColor(r.color, props.resources.indexOf(r)) }]} />
                  <Text style={styles.name} numberOfLines={1}>
                    {r.displayName}
                  </Text>
                  <Text style={styles.meta}>{open ? 'Con horario' : 'Sin horario'}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          <View style={styles.prefRow}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.prefLabel}>Mostrar cancelados</Text>
              <Text style={styles.prefHint}>Se ven apagados en el tablero y la lista.</Text>
            </View>
            <Switch
              value={props.showCancelled}
              onValueChange={props.onShowCancelledChange}
              accessibilityLabel="Mostrar turnos cancelados"
            />
          </View>

          <View style={styles.footer}>
            <TouchableOpacity style={styles.secondaryBtn} onPress={props.onClose}>
              <Text style={styles.secondaryBtnText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.primaryBtn, draft.length === 0 && styles.btnDisabled]}
              onPress={apply}
              disabled={draft.length === 0}
            >
              <Text style={styles.primaryBtnText}>Aplicar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    ...agendaPaperSheet,
    width: '100%',
    zIndex: 1,
    ...Platform.select({
      web: { boxShadow: '0 16px 48px rgba(0,0,0,0.28)' } as object,
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
        elevation: 8,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
    flexShrink: 0,
  },
  title: { fontSize: 16, fontWeight: '700', color: agenda.ink, fontFamily: agendaFonts.sans },
  subtitle: { fontSize: 12, color: agenda.ink2, marginTop: 4, lineHeight: 16, fontFamily: agendaFonts.sans },
  close: { fontSize: 24, lineHeight: 24, color: agenda.ink2, paddingHorizontal: 4 },
  count: {
    fontSize: 12,
    fontWeight: '700',
    color: agenda.ink2,
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 4,
    fontFamily: agendaFonts.sans,
    flexShrink: 0,
  },
  countBad: { color: ticketColors.bad },
  list: { flexGrow: 0, flexShrink: 1, minHeight: 0 },
  listContent: { paddingHorizontal: 8, paddingBottom: 8 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  rowOn: { backgroundColor: agenda.chooseBg, borderColor: agenda.chooseBorder },
  rowBlocked: { opacity: 0.45 },
  check: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: agenda.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: agenda.surface,
  },
  checkOn: { backgroundColor: agenda.brand, borderColor: agenda.brand },
  checkMark: { color: agenda.brandInk, fontSize: 11, fontWeight: '700' },
  dot: { width: 10, height: 10, borderRadius: 5 },
  name: { flex: 1, fontSize: 14, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  meta: { fontSize: 11, color: agenda.ink2, fontFamily: agendaFonts.sans },
  prefRow: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: agenda.border,
  },
  prefLabel: { fontSize: 13, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  prefHint: { fontSize: 11, color: agenda.ink2, marginTop: 1, fontFamily: agendaFonts.sans },
  footer: {
    flexShrink: 0,
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 12,
    borderTopWidth: 1,
    borderTopColor: agenda.border,
  },
  primaryBtn: {
    flex: 1,
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingVertical: 11,
    alignItems: 'center',
  },
  primaryBtnText: { fontWeight: '700', color: agenda.brandInk, fontFamily: agendaFonts.sans },
  secondaryBtn: {
    flex: 1,
    borderRadius: agenda.radius,
    paddingVertical: 11,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  secondaryBtnText: { fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  btnDisabled: { opacity: 0.5 },
});
